import { Button, Typography } from '@material-ui/core'
import { parseISO } from 'date-fns'
import React, { useContext, useEffect, useMemo } from 'react'
import styled from 'styled-components'

import { SanityCheckoutMarketingContentFragment } from '../../../../graphql/gatsby'
import useUserCategory from '../../../../hooks/useUserCategory'
import ConfirmationLayout from '../../../../layouts/buy/ConfirmationLayout'
import { CompletedCartsContext } from '../../../../lib/completed-carts/context'
import { useDispensaryContext } from '../../../../lib/dispensaries'
import { fromUpdateUserDispensaryRequest } from '../../../../lib/dispensaries/user'
import { usePageUrl } from '../../../../lib/pages'
import { formatIfValid } from '../../../../utils/dateTools'
import { logAndCaptureException } from '../../../../utils/errorTools'
import { userCategories } from '../../../../utils/userClaims'
import AddressSummary from '../../summary/AddressSummary'
import CreditCardPaymentMethodSummary from '../../summary/CreditCardPaymentMethodSummary'
import DeliveryMethodSummary from '../../summary/DeliveryMethodSummary'
import MobileCartItemsSummary from '../../summary/MobileCartItemsSummary'
import PurchaseOrderPaymentMethodSummary from '../../summary/PurchaseOrderPaymentMethodSummary'
import * as Styled from './styled'

export interface ConfirmationProps {
  className?: string
  completedAt: string
  checkoutMarketingContent: SanityCheckoutMarketingContentFragment['sanityCheckoutMarketingContent']
}

const Confirmation: React.FC<ConfirmationProps> = ({
  className,
  completedAt,
  checkoutMarketingContent,
}) => {
  const completedCarts = useContext(CompletedCartsContext)
  const completedCart = useMemo(
    () => completedCarts.find({ completedAt, mode: 'completedAt' }),
    [completedAt, completedCarts],
  )
  const { navigate } = usePageUrl()
  const dispensary = useDispensaryContext()
  const { userCategory } = useUserCategory()

  useEffect(() => {
    if (!completedCart) {
      navigate('/checkout')
    }
  }, [completedCart, navigate])

  useEffect(() => {
    if (!dispensary || !completedCart || userCategory !== userCategories.patient) {
      return
    }
    const { practitionerCode } = dispensary
    const subscription = fromUpdateUserDispensaryRequest({ practitionerCode }).subscribe({
      error: logAndCaptureException,
    })
    return () => subscription.unsubscribe()
  }, [dispensary, completedCart, userCategory])

  if (!completedCart) {
    return null
  }

  const { cart } = completedCart

  const {
    billing_address: billingAddress,
    shipping_addresses: [shippingAddress],
  } = cart
  const { selected_shipping_method: shippingMethod } = shippingAddress || {}

  return (
    <ConfirmationLayout
      cart={cart}
      className={className}
      checkoutMarketingContent={checkoutMarketingContent}
    >
      <Button
        color="secondary"
        id="continue-shopping-button"
        fullWidth
        onClick={() => navigate('/products')}
        type="submit"
        variant="contained"
      >
        CONTINUE SHOPPING
      </Button>
      <Styled.Heading item xs={12}>
        <Typography variant="h5" data-cy="order-confirmation">
          ORDER CONFIRMATION
        </Typography>
      </Styled.Heading>
      <Styled.ThankYou container item justify="center" xs={12}>
        <Typography variant="body2" gutterBottom>
          Thank you for your order.
        </Typography>
        <Styled.AllowDelay variant="body2" gutterBottom>
          Please allow up to 12 hours for your order to process and appear in your order history.
          Once it is processed an order confirmation email will be sent to you. For information on
          your order status, please call our Customer Experience team at (860) 623-6314.
        </Styled.AllowDelay>
      </Styled.ThankYou>
      <Styled.OrderDateSection
        name="Date"
        information={formatIfValid(parseISO(completedAt), 'P') || ''}
      />
      {shippingMethod && (
        <DeliveryMethodSummary
          description={`${shippingMethod.carrier_title} ${shippingMethod.method_title}`}
          shippingAmount={shippingMethod.amount?.value || 0}
          holdUntilMonday={cart.hold_until_monday || ''}
        />
      )}
      {shippingAddress && <AddressSummary address={shippingAddress} title="Shipping Address" />}
      {billingAddress && <AddressSummary address={billingAddress} title="Billing Address" />}
      <CreditCardPaymentMethodSummary selectedPaymentMethod={cart.selected_payment_method} />
      <PurchaseOrderPaymentMethodSummary selectedPaymentMethod={cart.selected_payment_method} />
      <Styled.MobileCaliforniaProp65Warning items={cart.items} shippingAddress={shippingAddress} />
      <MobileCartItemsSummary cart={cart} suppressHeader />
      <Styled.MobileOrderSummary cart={cart} totalConfirmed />
    </ConfirmationLayout>
  )
}

const StyledConfirmation = styled(Confirmation)(({ theme }) => ({
  '.MuiButton-containedSecondary': {
    marginBottom: '25px',
    marginLeft: '15px',
    marginRight: '15px',
    '&, &:hover': {
      backgroundColor: theme.colors.grayscale.white,
      border: `1px solid ${theme.colors.brandBlue.dark100}`,
      color: theme.colors.brandBlue.dark100,
      textDecoration: 'none',
    },
  },
  '@media(min-width: 600px)': {
    '.MuiButton-containedSecondary': {
      marginLeft: '0',
      marginRight: '0',
    },
  },
}))

export { StyledConfirmation as Confirmation }
